import {GaleAPIBridge, GaleEvent, ScreenName} from './bridge'
import {Sensor, Clinic, SensorMeasurementData, WeightScaleData, BloodPressureMeasurementData, User, PatientProfile, PdfFileData, GlucometerData} from '../types'

interface Context {
    language: string
    sensors: Sensor[]
    clinics: Clinic[]
    measurments: SensorMeasurementData[]
    startSessionTime: number | undefined
}

export async function init(bridge: GaleAPIBridge, startTime?:number) {
    console.log("initialize dom");
    async function submit(type: string, body: FormData) {
        console.log("submit dom function");
        const output = {} as {[key: string]: any}
        for (const entry of Array.from(body.entries())) {
            const [key, value] = entry as string[]
            const comps = key.split('.')
            for (let i = 0; i < comps.length - 1; ++i)
                output[comps[i]] = output[comps[i]] || {}

            const target = comps.slice(0, -1).reduce((a, o) => a[o], output)
            target[comps[comps.length - 1]] = value
        }
        console.log('await bridge '+type)
        await (bridge[type as keyof GaleAPIBridge] as any)(output)
    }

    console.log("init context");

    const context: Context = {
        language: 'en',
        sensors: await bridge.getSensors(),
        clinics: await bridge.getClinics(),
        measurments: await bridge.getSensorData(),
        startSessionTime: startTime
    }

    const checkLanguage = async () => {
        const lang = await bridge.getCurrentLanguage()
        context.language = lang
        document.documentElement.lang = lang
    }

    bridge.listen('languagechange', checkLanguage);
    await checkLanguage()
    const getPathFromHash = (hash: string) => {
        const match = hash.match(/\#gale\:\/?(.*)/)
        if (!match || !match[1])
            return null

        return match[1].split('/').map(decodeURIComponent)
    }

    window.addEventListener('hashchange', (e: HashChangeEvent) => {
        const path = getPathFromHash(location.hash)
        if (!path || !path[0])
            return
        bridge.navigate(path[0] as ScreenName, path[1])
        history.replaceState(null, "", `#${path[2] || ''}`)
    })

    class GaleForm extends HTMLElement {
        constructor(type: string) {
            super()

            const form = this.querySelector('form')
            if (!form)
                return
            form.addEventListener('submit', (e: Event) => {
                e.preventDefault();
                console.log("GALEform event"+JSON.stringify(e));
                (async () => {
                    try {
                        await submit(type, new FormData(form))
                        this.dispatchEvent(new Event('submitsuccess'))
                    } catch (e:any) {
                        const error = form.querySelector('.error') as HTMLElement;
                        if (error)
                            error.innerText = e.message
                        this.dispatchEvent(new Event('submitfail'))
                    }

                    })()
            })
        }
    }

    function formatSensorData(data: SensorMeasurementData, type: string, format?: string | null) {
        const  defaultFormats  = {
            "Weight Scale": '{weight} {unit}',
            "Thermometer": '{temperature}',
            "Glucometer": '{glucometerData} {stringUnit}',
            "Blood Pressure Monitor": '{highpressure}/{lowpressure} ({heartrate})',
            "Pulse Oximeter": 'O2:{oxygen} PR:{pulse} PI:{PI} RR:{respirationRate}',
            "Stethoscope": '{duration}ms'
        }

        if (type === "Glucometer") {
            let glucometerData = data as GlucometerData
            if (glucometerData.unit === 1) {
                glucometerData.stringUnit = "mmol/L"
            } else if (glucometerData.unit === 0) {
                glucometerData.stringUnit = "mg/dL"
            } else {
                glucometerData.stringUnit = ""
            }
            data = glucometerData
        }

        format = format || defaultFormats[type as keyof typeof defaultFormats]
        if (!format)
            return JSON.stringify(data)
        return format.replace(/\{([^}]+)\}/g, (a, b) => Reflect.has(data, b) ? Reflect.get(data, b) : b)
    }

    const noSpaces = (s: string) => s.replace(/\s/g, '').toUpperCase()
    class GaleSensorOutput extends HTMLElement {
        constructor() {
            super()
            const shadow = this.attachShadow({mode: 'open'})
            const output = document.createElement('output')
            shadow.appendChild(output)
            const slot = document.createElement('slot')
            let original = ''
            let assigned = null as HTMLElement | null
            slot.addEventListener('slotchange', (e: Event) => {
                if (slot.assignedElements().length) {
                    assigned = slot.assignedElements()[0] as HTMLElement
                    output.appendChild(assigned)
                    original = assigned.innerHTML
                }
            })
            output.appendChild(slot)
            let format =''
            format = format || this.getAttribute('format') || ''
            const sensors = context.sensors
            const sensor = sensors.find(s => s.type === this.getAttribute('type'))
            if (!sensor)
                return

            const thisType = noSpaces(sensor.type)
            const thisName = noSpaces(sensor.name)

            const updateMeasurementData = (data: SensorMeasurementData) => {
                if (typeof data.type === 'undefined') return
                const t = noSpaces(data.type)
                if (t ===  thisType || t === thisName || (t.indexOf(thisType)>=0)) {
                    output.innerHTML = formatSensorData(data, sensor.type, format)
                }
            }
            
            const updateAllMeasurementData = async () => {
                if (assigned)
                    assigned.innerHTML = original
                context.measurments = await bridge.getSensorData(context.startSessionTime)
            
                for (const value of context.measurments)
                    updateMeasurementData(value)
            }

            window.addEventListener('load', async (e) => {
                context.measurments = await bridge.getSensorData(context.startSessionTime)
                context.measurments.forEach(measurement => {
                    updateMeasurementData(measurement)
                })
            })

            bridge.listen('sessionchange', updateAllMeasurementData)

            bridge.listen('sensormeasurementdata', e => {  
                updateMeasurementData(e.data)
            })
            updateAllMeasurementData();
        }
    }

    class GaleList<ItemType extends object> extends HTMLElement {
        constructor(call: () => PromiseLike<ItemType[]>) {
            super()
            const template = Array.from(this.childNodes).find(t => t.nodeName.toUpperCase() === "TEMPLATE") as HTMLTemplateElement
            if (!template) {
                throw new Error(`Found list element without a template`)
            }

            const templateHTML = template.innerHTML
            
            this.update = () => call().then(items => {
                console.log({items, templateHTML})
                items.forEach((item) => {
                    const html = templateHTML.replace(/{{([^}]+)}}/ig, (_, key) =>
                        Reflect.has(item, key) ? item[key as keyof ItemType] : key
                    )
                    const fragment = document.createRange().createContextualFragment(html)
                    this.appendChild(fragment)
                })
            })

            this.update()
        }

        update() {}
    }

    class GaleSensorList extends GaleList<Sensor> {
        constructor() {
            super(() => bridge.getSensors())
        }
    }
    class GalePatientProfileList extends GaleList<PatientProfile> {
        constructor() {
            super(() => bridge.getPatientProfiles())

            bridge.addEventListener('loginchange', () => {
                this.update()
            })
        }
    }

    class GaleClinicList extends GaleList<Clinic> {
        constructor() {
            super(() => bridge.getClinics())
        }
    }

    class GalePrescriptionList extends GaleList<PdfFileData> {
        constructor() {
            super(() => bridge.getPrescriptionData())
        }
    }

    class GalePatientVisitSummaryList extends GaleList<PdfFileData> {
        constructor() {
            super(() => bridge.getPatientVisitSummaryData())
        }
    }

    class GaleIntakeForm extends GaleForm {
        constructor() {
            super('updateIntake')
        }
    }

    class GaleEmailForm extends GaleForm {
        constructor() {
            super('sendSummaryEmail')
        }
    }

    class GaleLoginForm extends GaleForm {
        constructor() {
            super('login')
            this.addEventListener('submitsuccess', () => {
                window.location.href = this.getAttribute('target') || '#'          
            })
        }
    }

    class GalePatientProfileForm extends GaleForm {
        constructor() {
            super('switchPatientProfile')
            this.addEventListener('submitsuccess', () => {
                window.location.href = this.getAttribute('target') || '#'          
            })
        }
    }

    class GaleSignupForm extends GaleForm {
        constructor() {
            super('signUp')
            this.addEventListener('submitsuccess', async () => {
                if (!this.hasAttribute('autologin'))
                    return

                const form = this.querySelector('form')
                if (!form)
                    return

                const data = new FormData(form)
                const email = data.get('email') as string
                const password = data.get('password') as string
                if (email && password) {
                    try {
                        await bridge.login({email, password})
                        window.location.href = this.getAttribute('target') || '#'
                    } catch (e) {
                        console.error(e)
                    }

                }
            })
        }
    }

    class GaleHealthHistoryForm extends GaleForm {
        constructor() {
            super('setIntakeHealthHistory')
        }
    }

    class GaleBloodPressureForm extends GaleForm {
        constructor() {
            super('addBloodPressureData')
        }
    }

    class GalePulseOximeterForm extends GaleForm {
        constructor() {
            super('addPulseOxData')
        }
    }

    class GaleThermometerForm extends GaleForm {
        constructor() {
            super('addThermometerData')
        }
    }

    class GaleGlucometerForm extends GaleForm {
        constructor() {
            super('addGlucometerData')
        }
    }

    class GaleWeightScaleForm extends GaleForm {
        constructor() {
            super('addWeightScaleData')
        }
    }

    class GaleSpirometerForm extends GaleForm {
        constructor() {
            super('addSpirometerData')
        }
    }

    class GaleMultifunctionForm extends GaleForm {
        constructor() {
            super('addMultifunctionData')
        }
    }

    class GaleUserElement extends HTMLElement {
        constructor() {
            super()
            const slot = document.createElement('slot')
            const shadow = this.attachShadow({mode: 'open'})
            shadow.appendChild(slot)
            let template = ''
            const update = async () => {
                if (!template){
                    return
                }
                const user = await bridge.getCurrentUser()
                if (!user) {
                    this.innerHTML = ''
                    return
                }
                    
                const html = template.replace(/\{([^}]+)\}/g, (a, b) => Reflect.has(user, b) ? Reflect.get(user, b) : b)
                 if (html === this.innerHTML)
                    return

                this.innerHTML = html
            }
            slot.addEventListener('slotchange', () => {
                if (!template){// only change the template if it's not initialized
                template = this.innerHTML
                }
                update()
            })
            bridge.listen("loginchange", () => {
                update()
            })
        }
    }

    class GaleLink extends HTMLElement {
        private anchor: HTMLAnchorElement
        private toHref: () => string
        constructor(toHref?: () => string) {
            super()

            this.toHref = toHref || (() => `#gale:${this.getAttribute('href')}`)
            const shadow = this.attachShadow({mode: 'open'})
            const a = document.createElement('a')
            const slot = document.createElement('slot')
            a.appendChild(slot)
            shadow.appendChild(a)
            this.anchor = a
        }

        static get observedAttributes() { return ['type'] }

        private render() {
            this.anchor.href = this.toHref()
        }

        connectedCallback() {
            this.render()
        }

        attributesChangedCallback() {
            this.render()
        }

    }

    class GaleSensorLink extends HTMLElement {
        private anchor: HTMLAnchorElement
        constructor() {
            super()

            this.anchor = document.createElement('a')
            const slot = document.createElement('slot')
            this.anchor.appendChild(slot)
            this.anchor.style.display = 'contents'
            const shadow = this.attachShadow({mode: 'open'})
            shadow.appendChild(this.anchor)
            this.anchor.addEventListener('click', () => {
                bridge.showSensor(this.getAttribute('type') as string)
                location.href = this.getAttribute('href') as string
            })
        }
    }

    class GalePatientProfileLink extends HTMLElement {
        constructor() {
            super()

            this.addEventListener('click', async () => {
                const patientName = this.getAttribute('name')
                const dateOfBirth = this.getAttribute('dateOfBirth')
                if (!patientName || !dateOfBirth)
                    return

                await bridge.switchPatientProfile({patientName, dateOfBirth: new Date(dateOfBirth)})
                location.href = this.getAttribute('target') || location.href
            })
        }
    }
    class GaleClinicLink extends HTMLElement {
        private anchor: HTMLAnchorElement
        constructor() {
            super()

            this.anchor = document.createElement('a')
            const slot = document.createElement('slot')
            this.anchor.appendChild(slot)
            this.anchor.style.display = 'contents'
            const shadow = this.attachShadow({mode: 'open'})
            shadow.appendChild(this.anchor)

            this.anchor.addEventListener('click', () => {
                bridge.navigate('clinic', this.getAttribute('name') as string)
            })
        }
    }
    class GaleRoot extends HTMLElement {
        constructor() {
            super()
            const checkUser = async () => {
                const user = await bridge.getCurrentUser()
                this.classList.toggle('logged-in', !!user)
            }
            bridge.listen('loginchange', checkUser);
            checkUser()
        }
    }

    customElements.define('gale-root', GaleRoot)

    customElements.define('gale-link', GaleLink)
    customElements.define('gale-sensor-link', GaleSensorLink)
    customElements.define('gale-patient-profile-link', GalePatientProfileLink)
    customElements.define('gale-clinic-link', GaleClinicLink)

    customElements.define('gale-intake-form', GaleIntakeForm)
    customElements.define('gale-patient-profile-form', GalePatientProfileForm)
    customElements.define('gale-login-form', GaleLoginForm)
    customElements.define('gale-signup-form', GaleSignupForm)
    customElements.define('gale-email-form', GaleEmailForm)
    customElements.define('gale-health-history-form', GaleHealthHistoryForm)
    customElements.define('gale-blood-pressure-form', GaleBloodPressureForm)
    customElements.define('gale-pulse-oximeter-form', GalePulseOximeterForm)
    customElements.define('gale-thermometer-form', GaleThermometerForm)
    customElements.define('gale-glucometer-form', GaleGlucometerForm)
    customElements.define('gale-weight-scale-form', GaleWeightScaleForm)
    customElements.define('gale-spirometer-form', GaleSpirometerForm)
    customElements.define('gale-multifunction-form', GaleMultifunctionForm)

    customElements.define('gale-sensor-output', GaleSensorOutput)
    customElements.define('gale-user', GaleUserElement)

    customElements.define('gale-sensor-list', GaleSensorList)
    customElements.define('gale-patient-profile-list', GalePatientProfileList)
    customElements.define('gale-clinic-list', GaleClinicList)
    customElements.define('gale-prescription-list', GalePrescriptionList)
    customElements.define('gale-patient-visit-summary-list', GalePatientVisitSummaryList)
}